import React from "react";
import StoreIcon from "@mui/icons-material/Store";
import WarningIcon from "@mui/icons-material/Warning";
import InfoIcon from "@mui/icons-material/Info";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import DeleteIcon from "@mui/icons-material/Delete";

interface StoreTheme {
  logo?: string;
  background_image_desktop?: string;
}

interface Store {
  id: number | string;
  name: string;
  theme?: StoreTheme;
}

interface CartItem {
  productId: number;
  branchId: number;
  name: string;
  price: number;
  quantity: number;
}

interface StoresWithoutDeliveryProps {
  storesWithoutDelivery: number[];
  storeItemsMap: Record<number, CartItem[]>;
  stores: Store[];
  isRTL: boolean;
  t: (key: string) => string;
  textAlignClass: string;
  currency: (value: number) => string;
  updateQuantity: (productId: number, branchId: number, quantity: number) => void;
  removeItem: (productId: number, branchId: number) => void;
  handleVisitStore: (storeId: number) => void;
}

const StoresWithoutDelivery: React.FC<StoresWithoutDeliveryProps> = ({
  storesWithoutDelivery,
  storeItemsMap,
  stores,
  isRTL,
  t,
  textAlignClass,
  currency,
  updateQuantity,
  removeItem,
  handleVisitStore,
}) => {
  return (
    <>
      {storesWithoutDelivery.length > 0 && (
        <div className="mb-10">
          {/* Title */}
          <div className={`flex items-center gap-2 mb-4 ${isRTL ? "flex-row-reverse" : ""}`}>
            <StoreIcon className="text-amber-600" />
            <h2 className="text-xl font-semibold text-amber-800">
              {t("cart.storesWithoutDelivery")}
            </h2>
          </div>

          {/* Info Box */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4">
            <div className={`flex items-start gap-2 ${isRTL ? "flex-row-reverse" : ""}`}>
              <InfoIcon className="text-amber-600 mt-0.5 flex-shrink-0" />
              <p className={`text-sm text-amber-800 ${textAlignClass}`}>
                {t("cart.storesWithoutDeliveryInfo")}
              </p>
            </div>
          </div>

          {/* Stores List */}
          {storesWithoutDelivery.map((storeId) => {
            const items = storeItemsMap[storeId] || [];
            const store = stores.find((s) => Number(s.id) === storeId) || null;
            const subtotal = items.reduce((s, it) => s + it.price * it.quantity, 0);

            return (
              <div
                key={storeId}
                className="bg-white rounded-2xl shadow-md p-5 mb-6 border border-amber-200"
              >
                {/* Store Header */}
                <div
                  className={`flex justify-between items-center border-b pb-2 mb-3 ${
                    isRTL ? "flex-row-reverse" : ""
                  }`}
                >
                  <div className={`flex items-center gap-3 ${isRTL ? "flex-row-reverse" : ""}`}>
                    <img
                      src={
                        store?.theme?.logo ||
                        store?.theme?.background_image_desktop ||
                        "/placeholder.png"
                      }
                      alt={store?.name}
                      className="w-14 h-14 object-cover rounded-lg flex-shrink-0"
                    />

                    <div>
                      <h2 className={`text-lg font-semibold ${textAlignClass}`}>
                        {store?.name || t("cart.unknownStore")}
                      </h2>

                      <div
                        className={`flex items-center gap-1 text-sm text-amber-600 ${
                          isRTL ? "flex-row-reverse" : ""
                        }`}
                      >
                        <WarningIcon fontSize="small" />
                        <span>{t("cart.noDeliveryAvailable")}</span>
                      </div>
                    </div>
                  </div>

                  <span className="text-neutral-600">
                    {currency(subtotal)} {t("currency.egp")}
                  </span>
                </div>

                {/* Items */}
                <div className="space-y-2">
                  {items.map((it) => (
                    <div
                      key={`${it.productId}-${it.branchId}`}
                      className={`flex justify-between items-center ${
                        isRTL ? "flex-row-reverse" : ""
                      }`}
                    >
                      <div
                        className={`truncate ${textAlignClass} ${
                          isRTL ? "ml-4" : "mr-4"
                        }`}
                      >
                        {it.name} × {it.quantity}
                      </div>

                      <div
                        className={`flex items-center gap-2 ${
                          isRTL ? "flex-row-reverse" : ""
                        }`}
                      >
                        <button
                          onClick={() =>
                            updateQuantity(it.productId, it.branchId, it.quantity - 1)
                          }
                          disabled={it.quantity <= 1}
                          className="text-gray-500 hover:text-primary disabled:opacity-50 p-1 rounded-full hover:bg-gray-100"
                        >
                          <RemoveIcon fontSize="small" />
                        </button>

                        <span className="w-8 text-center font-medium">
                          {it.quantity}
                        </span>

                        <button
                          onClick={() =>
                            updateQuantity(it.productId, it.branchId, it.quantity + 1)
                          }
                          className="text-gray-500 hover:text-primary p-1 rounded-full hover:bg-gray-100"
                        >
                          <AddIcon fontSize="small" />
                        </button>

                        <button
                          onClick={() => removeItem(it.productId, it.branchId)}
                          className="text-red-500 hover:text-red-700 p-1 rounded-full hover:bg-red-50"
                        >
                          <DeleteIcon fontSize="small" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Visit Store Button */}
                <div className="mt-4 pt-4 border-t border-amber-100">
                  <button
                    onClick={() => handleVisitStore(storeId)}
                    className={`w-full bg-amber-100 hover:bg-amber-200 text-amber-800 py-2 px-4 rounded-lg transition-all flex items-center justify-center gap-2 ${
                      isRTL ? "flex-row-reverse" : ""
                    }`}
                  >
                    <StoreIcon />
                    {t("cart.visitStore")}
                  </button>

                  <p
                    className={`text-xs text-center text-amber-600 mt-2 ${textAlignClass}`}
                  >
                    {t("cart.visitStoreHint")}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
};

export default StoresWithoutDelivery;
